import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ThemeOptions } from './theme-options';


import { ButtonsModule, BsModalRef } from 'ngx-bootstrap';
import { CollapseModule } from 'ngx-bootstrap';
import { TimepickerModule } from 'ngx-bootstrap';
import { TypeaheadModule } from 'ngx-bootstrap';
import { CarouselModule } from 'ngx-bootstrap';
import { BsDropdownModule } from 'ngx-bootstrap';
import { BsDatepickerModule } from 'ngx-bootstrap';
import { ModalModule } from 'ngx-bootstrap';
import { PaginationModule } from 'ngx-bootstrap';
import { ProgressbarModule } from 'ngx-bootstrap';
import { TabsModule } from 'ngx-bootstrap';
import { PopoverModule } from 'ngx-bootstrap';
import { TooltipModule } from 'ngx-bootstrap';
import { AccordionModule } from 'ngx-bootstrap';
import { AlertModule } from 'ngx-bootstrap';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: false
};

import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { AngularEditorModule } from '@kolkov/angular-editor';

// Angular Circle Progress
import { NgCircleProgressModule } from 'ng-circle-progress';
import { LaddaModule } from 'angular2-ladda';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { ContextMenuModule } from 'ngx-contextmenu';
import { Ng2TableModule } from 'ng2-table';
import { Ng2SmartTableModule } from 'ng2-smart-table';
import { Ng2CompleterModule } from 'ng2-completer';
import { ClipboardModule } from 'ngx-clipboard';
import { NgSelectModule } from '@ng-select/ng-select';
import { AutosizeModule } from 'ngx-autosize';
import { ChartsModule } from 'ng2-charts/ng2-charts';
import { StationsComponent } from './pages/stations/stations.component';


// FontAwesome Regular SVG Icons
import { faSquare, faCheckCircle, faTimesCircle, faDotCircle, faThumbsUp, faComments, faFolderOpen, faTrashAlt, faFileImage, faFileArchive, faLifeRing, faCommentDots, faFolder, faKeyboard, faCalendarAlt, faEnvelope, faAddressCard, faMap, faObjectGroup, faImages, faUser, faLightbulb, faGem, faClock, faUserCircle, faQuestionCircle, faBuilding, faBell, faFileExcel, faFileAudio, faFileVideo, faFileWord, faFilePdf, faFileCode, faFileAlt, faEye, faChartBar } from '@fortawesome/free-regular-svg-icons';
import { faChevronRight, faSitemap, faPrint, faMapMarkerAlt, faTachometerAlt, faAlignCenter, faExternalLinkAlt, faShareSquare, faInfoCircle, faSync, faQuoteRight, faStarHalfAlt, faShapes, faCarBattery, faTable, faCubes, faPager, faCameraRetro, faBomb, faNetworkWired, faBusAlt, faBirthdayCake, faEyeDropper, faUnlockAlt, faDownload, faAward, faPlayCircle, faReply, faUpload, faBars, faEllipsisV, faSave, faSlidersH, faCaretRight, faChevronUp, faPlus, faLemon, faChevronLeft, faTimes, faChevronDown, faFilm, faSearch, faEllipsisH, faCog, faArrowsAltH, faPlusCircle, faAngleRight, faAngleUp, faAngleLeft, faAngleDown, faArrowUp, faArrowDown, faArrowRight, faArrowLeft, faStar, faSignOutAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faTwitter, faAngular, faVuejs, faReact, faHtml5, faGoogle, faInstagram, faPinterest, faYoutube, faDiscord, faSlack, faDribbble, faGithub } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';


import { NgProgressModule } from '@ngx-progressbar/core';
import { NgProgressRouterModule } from '@ngx-progressbar/router';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { NotifierModule, NotifierOptions } from 'angular-notifier';
import { NgxPrintModule } from 'ngx-print';

const customNotifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: 'right',
      distance: 12
    },
    vertical: {
      position: 'top',
      distance: 12,
      gap: 10
    }
  },
  theme: 'uifort',
  behaviour: {
    autoHide: 4000,
    onClick: 'hide',
    onMouseover: 'pauseAutoHide',
    showDismissButton: true,
    stacking: 4
  },
  animations: {
    enabled: true,
    show: {
      preset: 'slide',
      speed: 300,
      easing: 'ease'
    },
    hide: {
      preset: 'fade',
      speed: 300,
      easing: 'ease',
      offset: 50
    },
    shift: {
      speed: 300,
      easing: 'ease'
    },
    overlap: 150
  }
};

import { NgxPaginationModule } from 'ngx-pagination';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ImageCropperModule } from 'ngx-image-cropper';
// Input Mask
import { TextMaskModule } from 'angular2-text-mask';
import { ArchwizardModule } from 'angular-archwizard';

// Layouts
import { LeftSidebarComponent } from './layout-blueprints/left-sidebar/left-sidebar.component';
import { CollapsedSidebarComponent } from './layout-blueprints/collapsed-sidebar/collapsed-sidebar.component';
import { MinimalLayoutComponent } from './layout-blueprints/minimal-layout/minimal-layout.component';
import { PresentationLayoutComponent } from './layout-blueprints/presentation-layout/presentation-layout.component';

// Layout components
import { HeaderComponent } from './layout-components/header/header.component';
import { HeaderDotsComponent } from './layout-components/header-dots/header-dots.component';
import { HeaderDrawerComponent } from './layout-components/header-drawer/header-drawer.component';
import { HeaderUserboxComponent } from './layout-components/header-userbox/header-userbox.component';
import { HeaderSearchComponent } from './layout-components/header-search/header-search.component';
import { HeaderMenuComponent } from './layout-components/header-menu/header-menu.component';
import { SidebarCollapsedComponent } from './layout-components/sidebar-collapsed/sidebar-collapsed.component';
import { SidebarComponent } from './layout-components/sidebar/sidebar.component';
import { SidebarHeaderComponent } from './layout-components/sidebar-header/sidebar-header.component';
import { SidebarUserboxComponent } from './layout-components/sidebar-userbox/sidebar-userbox.component';
import { SidebarMenuComponent } from './layout-components/sidebar-menu/sidebar-menu.component';
import { SidebarFooterComponent } from './layout-components/sidebar-footer/sidebar-footer.component';
import { PageTitleComponent } from './layout-components/page-title/page-title.component';
import { ThemeConfiguratorComponent } from './layout-components/theme-configurator/theme-configurator.component';
import { PromoSectionComponent } from './layout-components/promo-section/promo-section.component';
import { ExampleWrapperSimpleComponent } from './layout-components/example-wrapper-simple/example-wrapper-simple.component';
import { ExampleWrapperSeamlessComponent } from './layout-components/example-wrapper-seamless/example-wrapper-seamless.component';
import { ExampleWrapperContainerComponent } from './layout-components/example-wrapper-container/example-wrapper-container.component';
import { DashboardNewComponent } from './pages/dashboard-new/dashboard-new.component';
import { GreetsComponent } from './pages/greets/greets.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { LoginComponent } from './pages/login/login.component';

// components
import { AngularMaterialModule } from './angular-material.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ng4LoadingSpinnerModule } from 'ng4-loading-spinner';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';

/* Angular Material */
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FlexLayoutModule } from "@angular/flex-layout";
import { InterceptorService } from './services/interceptor.service';
import { JwtModule } from '@auth0/angular-jwt';
import { DatePipe } from '@angular/common';
import { LayoutModule } from '@angular/cdk/layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { EmployeeLeavesComponent } from './pages/employee-leaves/employee-leaves.component';
import { TimeSheetComponent } from './pages/time-sheet/time-sheet.component';
import { TableFilterPipe } from './pages/employee-leaves/TableFilterPipe';
import { BookingsNewComponent } from './pages/bookings-new/bookings-new.component';
import { AssignedGreetsComponent } from './pages/greets/assigned-greets/assigned-greets.component';
import { UnAssignedGreetsComponent } from './pages/greets/un-assigned-greets/un-assigned-greets.component';
import { AssignGreetDialogComponent } from './pages/greets/assign-greet-dialog/assign-greet-dialog.component';
import { DataTableModule } from "@pascalhonegger/ng-datatable";
import { AgentSlotsCalenderComponent } from './pages/agent-slots-calender/agent-slots-calender.component';
import { NgbModalModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DeleteConfirmModalComponent } from './pages/modal-pop-ups/delete-confirm-modal/delete-confirm-modal.component';
import { ForgotPasswordNewComponent } from './pages/forgot-password-new/forgot-password-new.component';
import { WarningConfirmModalComponent } from './pages/modal-pop-ups/warning-confirm-modal/warning-confirm-modal.component';
import { EmployeesNewComponent } from './pages/employees-new/employees-new.component';
import { AddEmployeeComponent } from './pages/add-employee/add-employee.component';
import { SendEmailsComponent } from './pages/send-emails/send-emails.component';
import { CustomPipeModule } from './pipe/custom-pipe.module';
import { CancelledGreetsComponent } from './pages/greets/cancelled-greets/cancelled-greets.component';
import { NotificationsComponent } from './pages/notifications/notifications.component';
import { GreetRequestDialogComponent } from './pages/greet-request-dialog/greet-request-dialog.component';
import { AgentReportsComponent } from './pages/agent-reports/agent-reports.component';
import { AssignConfirmOverlapComponent } from './pages/greets/assign-confirm-overlap/assign-confirm-overlap.component';
import { VersionComponent } from './pages/version/version.component';
import { ContractsComponent } from './pages/contracts/contracts.component';
import { PromoServicesComponent } from './pages/promo-services/promo-services.component';
import { AddContractServiceComponent } from './pages/add-contract-service/add-contract-service.component';
import { AddPromoServiceComponent } from './pages/add-promo-service/add-promo-service.component';
import { AddContractsComponent } from './pages/add-contracts/add-contracts.component';
import { AddAdminComponent } from './pages/add-admin/add-admin.component';
import { ArDashboardComponent } from './pages/ar-dashboard/ar-dashboard.component';
import { ArUnassignedComponent } from './pages/ar-unassigned/ar-unassigned.component';
import { ArAssignedComponent } from './pages/ar-assigned/ar-assigned.component';
import { ArPaidComponent } from './pages/ar-paid/ar-paid.component';
import { ArLinkUnComponent } from './pages/ar-link-un/ar-link-un.component';
import { ArLinkInprogressComponent } from './pages/ar-link-inprogress/ar-link-inprogress.component';
import { ArInvoiceComponent } from './pages/ar-invoice/ar-invoice.component';
import { ArRwAdditionalComponent } from './pages/ar-rw-additional/ar-rw-additional.component';
import { ServiceAddDialogComponent } from './pages/service-add-dialog/service-add-dialog.component';
import { AddCustomerServiceComponent } from './pages/add-customer-service/add-customer-service.component';
import { CustomersComponent } from './pages/customers/customers.component';
import { AgenciesComponent } from './pages/agencies/agencies.component';
import { AddAgencyServiceComponent } from './pages/add-agency-service/add-agency-service.component';
import { TaxComponent } from './pages/tax/tax.component';
import { AddTaxServiceComponent } from './pages/add-tax-service/add-tax-service.component';
import { ArChartsComponent } from './pages/ar-charts/ar-charts.component';
import { ArReportsComponent } from './pages/ar-reports/ar-reports.component';
import { AddStationComponent } from './pages/add-station/add-station.component';
import { YesNoConfirmationComponent } from './pages/modal-pop-ups/yes-no-confirmation/yes-no-confirmation.component';
import { YesNoConfirmation1Component } from './pages/modal-pop-ups/yes-no-confirmation1/yes-no-confirmation1.component';
import { AirlinesComponent } from './pages/airlines/airlines.component';
import { AddAirlinesComponent } from './pages/add-airlines/add-airlines.component';
import { OutsourcedAgencyComponent } from './pages/outsourced-agency/outsourced-agency.component';
import { AddOutsourcedAgencyComponent } from './pages/add-outsourced-agency/add-outsourced-agency.component';
import { CreditCardDirectivesModule } from 'angular-cc-library';
import { CustomerPaidComponent } from './pages/customer-paid/customer-paid.component';
import { CustomerAssignedComponent } from './pages/customer-assigned/customer-assigned.component';
import { CustomerDashboardComponent } from './pages/customer-dashboard/customer-dashboard.component';
import { ArCustomerTransactionComponent } from './pages/ar-customer-transaction/ar-customer-transaction.component';
import { ArCustomerInvoiceComponent } from './pages/ar-customer-invoice/ar-customer-invoice.component';
import { CustomerChartsDashboardComponent } from './pages/customer-charts-dashboard/customer-charts-dashboard.component';
import { ReservationReportsComponent } from './pages/reservation-reports/reservation-reports.component';
import { SettingHomeComponent } from './pages/setting-home/setting-home.component';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { MonthlyInvoiceReportsComponent } from './pages/monthly-invoice-reports/monthly-invoice-reports.component';
import { FiltersComponent } from './pages/filters/filters.component';

export function tokenGetter() {
  return localStorage.getItem("token");
}

export function minlengthValidationMessages(err, field) {
  return `Should have atleast ${field.templateOptions.minLength} characters`;
}
export function maxlengthValidationMessages(err, field) {
  return `Should not exceed  ${field.templateOptions.maxLength} characters`;
}

@NgModule({
  declarations: [
    AppComponent,
    // Layout components
    HeaderComponent,
    HeaderDotsComponent,
    HeaderDrawerComponent,
    HeaderUserboxComponent,
    HeaderSearchComponent,
    HeaderMenuComponent,
    SidebarCollapsedComponent,
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarUserboxComponent,
    SidebarMenuComponent,
    SidebarFooterComponent,
    PageTitleComponent,
    ThemeConfiguratorComponent,
    PromoSectionComponent,
    ExampleWrapperSimpleComponent,
    ExampleWrapperSeamlessComponent,
    ExampleWrapperContainerComponent,

    // Layouts

    LeftSidebarComponent,
    CollapsedSidebarComponent,
    MinimalLayoutComponent,
    PresentationLayoutComponent,
    DashboardNewComponent,
    GreetsComponent,
    SettingsComponent,
    LoginComponent,
    EmployeesNewComponent,
    AddEmployeeComponent,
    EmployeeLeavesComponent,
    TimeSheetComponent,
    TableFilterPipe,
    BookingsNewComponent,
    AssignedGreetsComponent,
    UnAssignedGreetsComponent,
    CancelledGreetsComponent,
    AgentSlotsCalenderComponent,
    DeleteConfirmModalComponent,
    ForgotPasswordNewComponent,
    WarningConfirmModalComponent,
    SendEmailsComponent,
    NotificationsComponent,
    GreetRequestDialogComponent,
    ServiceAddDialogComponent,
    AgentReportsComponent,
    VersionComponent,
    ContractsComponent,
    PromoServicesComponent,
    AddContractServiceComponent,
    AddPromoServiceComponent,
    AddContractsComponent,
    AddAdminComponent,
    ArDashboardComponent,
    ArUnassignedComponent,
    ArAssignedComponent,
    ArPaidComponent,
    ArLinkUnComponent,
    ArLinkInprogressComponent,
    ArInvoiceComponent,
    ArRwAdditionalComponent,
    ServiceAddDialogComponent,
    AddCustomerServiceComponent,
    CustomersComponent,
    AgenciesComponent,
    AddAgencyServiceComponent,
    TaxComponent,
    AddTaxServiceComponent,
    ArChartsComponent,
    ArReportsComponent,
    StationsComponent,
    AddStationComponent,
    YesNoConfirmationComponent,
    YesNoConfirmation1Component,
    AirlinesComponent,
    AddAirlinesComponent,
    OutsourcedAgencyComponent,
    AddOutsourcedAgencyComponent,
    CustomerDashboardComponent,
    CustomerAssignedComponent,
    CustomerPaidComponent,
    ArCustomerTransactionComponent,
    ArCustomerInvoiceComponent,
    CustomerChartsDashboardComponent,
    ReservationReportsComponent,
    SettingHomeComponent,
    MonthlyInvoiceReportsComponent,
    FiltersComponent
  ],
  imports: [
    CreditCardDirectivesModule,
    BrowserModule,
    NgxPrintModule,
    CommonModule,
    HttpClientModule,
    AppRoutingModule,
    ChartsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    AngularMaterialModule,
    ButtonsModule.forRoot(),
    CollapseModule.forRoot(),
    TimepickerModule.forRoot(),
    TypeaheadModule.forRoot(),
    CarouselModule.forRoot(),
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    ModalModule.forRoot(),
    PaginationModule.forRoot(),
    ProgressbarModule.forRoot(),
    TabsModule.forRoot(),
    PopoverModule.forRoot(),
    TooltipModule.forRoot(),
    AccordionModule.forRoot(),
    AlertModule.forRoot(),
    PerfectScrollbarModule,
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'required', message: 'This field is required' },
        { name: 'minlength', message: minlengthValidationMessages },
        { name: 'maxlength', message: maxlengthValidationMessages },
      ],
    }),
    FormlyBootstrapModule,
    ArchwizardModule,
    AngularEditorModule,
    NgCircleProgressModule.forRoot(),
    LaddaModule,
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
    SlickCarouselModule,
    ContextMenuModule.forRoot(),
    Ng2TableModule,
    Ng2SmartTableModule,
    Ng2CompleterModule,
    ClipboardModule,
    NgSelectModule,
    AutosizeModule,
    FontAwesomeModule,
    NgxSkeletonLoaderModule,
    NgProgressModule,
    NgProgressRouterModule,
    NgxSpinnerModule,
    SweetAlert2Module.forRoot(),
    NotifierModule.withConfig(customNotifierOptions),
    NgxPaginationModule,
    InfiniteScrollModule,
    ImageCropperModule,
    TextMaskModule,
    NgbModalModule,
    NgbTooltipModule,
    FlexLayoutModule,
    AngularSvgIconModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        whitelistedDomains: ["example.com"],
        blacklistedRoutes: ["example.com/examplebadroute/"]
      }
    }),
    Ng4LoadingSpinnerModule.forRoot(),
    LayoutModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    DataTableModule,
    CustomPipeModule,
    NgbAccordionModule
  ],
  providers: [
    BsModalRef,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    DatePipe,
    ThemeOptions
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],

  entryComponents: [
    WarningConfirmModalComponent,
    AssignGreetDialogComponent,
    GreetRequestDialogComponent,
    AssignConfirmOverlapComponent,
    ServiceAddDialogComponent,
    AgentSlotsCalenderComponent,
    DeleteConfirmModalComponent,
    YesNoConfirmationComponent,
    YesNoConfirmation1Component
  ]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(faFacebook, faPrint, faAlignCenter, faMapMarkerAlt, faTachometerAlt, faExternalLinkAlt, faShareSquare, faSitemap, faInfoCircle, faLifeRing, faTwitter, faQuoteRight, faStarHalfAlt, faSync, faShapes, faCarBattery, faTable, faCubes, faPager, faAngular, faVuejs, faReact, faHtml5, faCheckCircle, faTimesCircle, faBomb, faNetworkWired, faBusAlt, faBirthdayCake, faEyeDropper, faThumbsUp, faCameraRetro, faUnlockAlt, faDownload, faUpload, faReply, faGoogle, faFileImage, faFolderOpen, faBars, faTrashAlt, faSave, faPlayCircle, faEllipsisV, faEllipsisH, faSlidersH, faFileArchive, faAward, faCaretRight, faInstagram, faPinterest, faYoutube, faDiscord, faSlack, faDribbble, faGithub, faPlus, faFolder, faTimes, faEnvelope, faAddressCard, faMap, faCalendarAlt, faImages, faFilm, faClock, faSearch, faChevronRight, faChevronUp, faChevronLeft, faChevronDown, faLink, faLightbulb, faGem, faCog, faDotCircle, faArrowsAltH, faComments, faCommentDots, faKeyboard, faObjectGroup, faUser, faUserCircle, faQuestionCircle, faBuilding, faBell, faFileExcel, faFileAudio, faFileVideo, faFileWord, faFilePdf, faFileCode, faFileAlt, faEye, faChartBar, faPlusCircle, faAngleRight, faAngleUp, faAngleLeft, faAngleDown, faArrowUp, faArrowDown, faArrowRight, faArrowLeft, faStar, faSignOutAlt, faLemon);
  }
}
